import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserIcon,
  SunIcon,
  MoonIcon,
  ArrowPathIcon,
  Bars3Icon,
  BellIcon,
  MagnifyingGlassIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { getLayoutStyle } from './layoutStyles';
import './styles/Navbar.css';
import NotificationPanel from './NotificationPanel';
import { useNotifications } from '../../context/NotificationContext';

// Helper: get user initials
const getInitials = (user) => {
  const first = user?.firstname || user?.full_name?.split(' ')[0] || '';
  const last = user?.lastname || user?.full_name?.split(' ')[1] || '';
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || 'U';
};

// Helper: darken hex color
const darkenHex = (hex, pct) => {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = Math.max(0, (n >> 16) - Math.round(255 * pct / 100));
  const g = Math.max(0, ((n >> 8) & 0xff) - Math.round(255 * pct / 100));
  const b = Math.max(0, (n & 0xff) - Math.round(255 * pct / 100));
  return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`;
};

// Map route paths to breadcrumb labels
const routeLabels = {
  '/central-dashboard': 'Command Center',
  '/enduser-dashboard': 'My Portal',
  '/compliance': 'Trust Center',
  '/risk-management': 'Risk Management',
  '/collaboration': 'Team Collaboration',
  '/documents': 'Document Library',
  '/integrations': 'AI Asset Manager',
  '/ai-dspm': 'AI DSPM',
  '/ai-tprm': 'AI TPRM',
  '/ai-vapt': 'AI VAPT',
  '/ai-crm': 'AI CRM',
  '/ai-cspm': 'AI CSPM',
  '/SOC': 'AI Virtual SOC',
  '/ai-lms': 'AI LMS',
  '/reports': 'AI Analytics',
  '/incident-reporting': 'Incident Reporting',
  '/security': 'Security Dashboard',
  '/admin': 'Administration',
  '/settings': 'Settings',
};

export const Navbar = ({
  user,
  onToggleSidebar,
  onToggleTheme,
  onSwitchCompany,
  onLogout,
  isDarkMode,
  greeting,
  showCompanySwitch = false,
  showThemeToggle = true,
  layoutStyle = 'classic',
  primaryColor = '#1E3A5F',
  onOpenSearch,
  themeMode = 'light',
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const stylePreset = getLayoutStyle(layoutStyle);
  const navPreset = stylePreset.navbar;
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Notification context (safe defaults when provider not yet mounted)
  const { unreadCount = 0, openPanel } = useNotifications();

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navBg = isDarkMode ? navPreset.bgDark : navPreset.bg;
  const navText = isDarkMode ? navPreset.textDark : navPreset.text;
  const navBorder = isDarkMode ? navPreset.borderDark : navPreset.border;
  const navShadow = isDarkMode ? navPreset.shadowDark : navPreset.shadow;
  const darkerPrimary = darkenHex(primaryColor, 10);

  const hexToRgbComponents = (hex) => {
    const n = parseInt(hex.replace('#', ''), 16);
    return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
  };

  const navbarInlineStyle = {
    background: navBg,
    borderBottomColor: navBorder,
    borderBottomWidth: navPreset.style === 'transparent' ? '0' : '1px',
    borderBottomStyle: 'solid',
    boxShadow: navShadow,
    height: navPreset.height,
    '--app-primary-rgb': hexToRgbComponents(primaryColor),
    ...(navPreset.backdrop ? { backdropFilter: navPreset.backdrop, WebkitBackdropFilter: navPreset.backdrop } : {}),
  };

  // Breadcrumb from current route
  const currentPage = routeLabels[location.pathname] || location.pathname.replace('/', '').replace(/-/g, ' ');

  return (
    <header
      className={`navbar navbar-layout-${layoutStyle} ${isDarkMode ? 'navbar-dark' : 'navbar-light'}`}
      style={navbarInlineStyle}
    >
      {/* Left Section */}
      <div className="navbar-section navbar-section-left">
        {/* Mobile Menu Button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="navbar-menu-btn"
          onClick={onToggleSidebar}
          aria-label="Toggle menu"
          title="Toggle menu"
        >
          <Bars3Icon className="navbar-menu-icon" />
        </motion.button>

        {/* Breadcrumb / Page Title */}
        <div className="navbar-breadcrumb">
          <span className="navbar-breadcrumb-greeting" style={{ color: isDarkMode ? '#94A3B8' : '#94A3B8' }}>
            {greeting}
          </span>
          <div className="navbar-breadcrumb-path">
            <span className="navbar-breadcrumb-root" style={{ color: isDarkMode ? '#64748B' : '#94A3B8' }}>Home</span>
            <ChevronRightIcon className="navbar-breadcrumb-separator" style={{ color: isDarkMode ? '#475569' : '#CBD5E1' }} />
            <span className="navbar-breadcrumb-current" style={{ color: navText }}>{currentPage}</span>
          </div>
        </div>
      </div>

      {/* Center - Search (clickable trigger for Command Palette) */}
      <div className="navbar-center">
        <button
          type="button"
          className="navbar-search"
          onClick={onOpenSearch}
          style={{
            backgroundColor: isDarkMode ? '#1E293B' : '#F8FAFC',
            borderColor: isDarkMode ? '#334155' : '#E2E8F0',
            cursor: 'pointer',
          }}
        >
          <MagnifyingGlassIcon className="navbar-search-icon" style={{ color: isDarkMode ? '#64748B' : '#94A3B8' }} />
          <span className="navbar-search-input" style={{ color: isDarkMode ? '#64748B' : '#94A3B8' }}>
            Search modules, settings...
          </span>
          <kbd className="navbar-search-shortcut" style={{
            backgroundColor: isDarkMode ? '#0F172A' : '#F1F5F9',
            color: isDarkMode ? '#64748B' : '#94A3B8',
            borderColor: isDarkMode ? '#334155' : '#E2E8F0',
          }}>⌘K</kbd>
        </button>
      </div>

      {/* Right Section */}
      <div className="navbar-section navbar-section-right">
        {/* Action Buttons */}
        <div className="navbar-actions">
          {/* Theme Toggle */}
          {showThemeToggle && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="navbar-action-btn"
              onClick={onToggleTheme}
              title={themeMode === 'dark' ? 'Switch to Light' : 'Switch to Dark'}
              aria-label={`Current: ${themeMode} mode`}
            >
              {themeMode === 'dark' ? (
                <SunIcon className="navbar-action-icon" />
              ) : (
                <MoonIcon className="navbar-action-icon" />
              )}
            </motion.button>
          )}

          {/* Notifications */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="navbar-action-btn navbar-notification-btn"
            title="Notifications"
            aria-label="View notifications"
            onClick={() => openPanel?.()}
          >
            <BellIcon className="navbar-action-icon" />
            {unreadCount > 0 && (
              <span
                className="navbar-notification-badge"
                style={{
                  position: 'absolute',
                  top: '4px',
                  right: '4px',
                  minWidth: 16,
                  height: 16,
                  borderRadius: 8,
                  background: '#EF4444',
                  color: '#fff',
                  fontSize: 9,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 3px',
                  lineHeight: 1,
                  border: '2px solid transparent',
                }}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </motion.button>

          {/* Company Switch */}
          {showCompanySwitch && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="navbar-action-btn"
              onClick={onSwitchCompany}
              title="Switch Company"
              aria-label="Switch company"
            >
              <ArrowPathIcon className="navbar-action-icon" />
            </motion.button>
          )}
        </div>

        {/* Divider */}
        <div className="navbar-divider" />

        {/* User Profile Dropdown */}
        <div className="navbar-user-profile" ref={dropdownRef}>
          <button
            className="navbar-user-trigger"
            onClick={() => setShowUserDropdown(!showUserDropdown)}
          >
            {/* Avatar with initials */}
            <div
              className="navbar-user-avatar"
              style={{
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${darkerPrimary} 100%)`,
              }}
            >
              <span className="navbar-user-avatar-text">{getInitials(user)}</span>
            </div>
            {/* Name */}
            <div className="navbar-user-info">
              <span className="navbar-user-name" style={{ color: navText }}>
                {user?.firstname || user?.full_name?.split(' ')[0] || 'User'}
              </span>
            </div>
            <svg className="navbar-user-chevron" style={{ color: isDarkMode ? '#64748B' : '#94A3B8' }} width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {/* Dropdown */}
          <AnimatePresence>
            {showUserDropdown && (
              <motion.div
                initial={{ opacity: 0, y: -6, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -6, scale: 0.97 }}
                transition={{ duration: 0.14, ease: [0.16, 1, 0.3, 1] }}
                className="navbar-user-dropdown"
                style={{
                  backgroundColor: isDarkMode ? '#0F172A' : '#FFFFFF',
                  borderColor: isDarkMode ? '#1E293B' : '#E2E8F0',
                  boxShadow: isDarkMode
                    ? '0 0 0 1px rgba(255,255,255,0.06), 0 8px 24px rgba(0,0,0,0.55), 0 32px 64px rgba(0,0,0,0.4)'
                    : '0 0 0 1px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.08), 0 32px 64px rgba(0,0,0,0.06)',
                }}
              >
                {/* ── Header ── */}
                <div className="navbar-dropdown-header" style={{ borderBottomColor: isDarkMode ? '#1E293B' : '#F1F5F9' }}>
                  <div className="navbar-dropdown-avatar-wrap">
                    <div
                      className="navbar-dropdown-avatar"
                      style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${darkerPrimary} 100%)` }}
                    >
                      {getInitials(user)}
                    </div>
                    <span className="navbar-dropdown-avatar-online" />
                  </div>
                  <div className="navbar-dropdown-user-meta">
                    <p className="navbar-dropdown-name" style={{ color: navText }}>
                      {user?.firstname || user?.full_name || 'User'}{user?.lastname ? ` ${user.lastname}` : ''}
                    </p>
                    <p className="navbar-dropdown-email" style={{ color: isDarkMode ? '#475569' : '#94A3B8' }}>
                      {user?.email || ''}
                    </p>
                  </div>
                </div>

                {/* ── Body items ── */}
                <div className="navbar-dropdown-body">
                  <button
                    className="navbar-dropdown-item"
                    onClick={() => { setShowUserDropdown(false); navigate(user?.role === 'admin' || user?.role === 'platform_admin' ? '/admin' : '/settings'); }}
                    style={{ color: isDarkMode ? '#CBD5E1' : '#374151' }}
                  >
                    <Cog6ToothIcon className="navbar-dropdown-item-icon" />
                    <span className="navbar-dropdown-item-label">Settings</span>
                    <span className="navbar-dropdown-item-arrow">›</span>
                  </button>

                  {showCompanySwitch && (
                    <button
                      className="navbar-dropdown-item"
                      onClick={() => { setShowUserDropdown(false); onSwitchCompany?.(); }}
                      style={{ color: isDarkMode ? '#CBD5E1' : '#374151' }}
                    >
                      <ArrowPathIcon className="navbar-dropdown-item-icon" />
                      <span className="navbar-dropdown-item-label">Switch Company</span>
                      <span className="navbar-dropdown-item-arrow">›</span>
                    </button>
                  )}
                </div>

                {/* ── Footer / Sign out ── */}
                {onLogout && (
                  <div className="navbar-dropdown-footer" style={{ borderTopColor: isDarkMode ? '#1E293B' : '#F1F5F9' }}>
                    <button
                      className="navbar-dropdown-item navbar-dropdown-item-danger"
                      onClick={() => { setShowUserDropdown(false); onLogout(); }}
                    >
                      <ArrowRightOnRectangleIcon className="navbar-dropdown-item-icon" />
                      <span className="navbar-dropdown-item-label">Sign Out</span>
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  );
};

/* NotificationPanel is rendered here so it sits above all content but
   is naturally positioned relative to the navbar button (top-right) */
export const NavbarWithNotifications = (props) => (
  <>
    <Navbar {...props} />
    <NotificationPanel isDarkMode={props.isDarkMode} primaryColor={props.primaryColor} />
  </>
);

export const getRoleDisplayName = (role) => {
  const roleMap = {
    platform_admin: 'Platform Admin',
    customer_admin: 'Customer Admin',
    security_analyst: 'Security Analyst',
    auditor: 'Auditor',
    end_user: 'End User',
    admin: 'Admin',
    company_user: 'Company User',
  };

  return roleMap[role] || role || 'User';
};

export default Navbar;
